import DetailsCard from "@/components/DetailsCard";
import DetailsCardThreeColumnRow from "@/components/DetailsCardThreeColumnRow";
import MetricGrid from "@/components/MetricGrid";
import { METRIC_CARD_MIN_WIDTH_PX } from "@/components/MetricGrid/metricGridLayout";
import SectionHeading from "@/components/SectionHeading";
import {
  buildApplicationsOverviewOptions,
  buildCareerOverviewMetrics,
  buildCompensationOverviewOptions,
  buildPreferencesOverviewOptions,
  getCurrentCompensationProfile,
  getActiveGovernanceRoles,
  type CareerOverviewCompensationProfile,
  type CareerOverviewGovernanceRole,
  type CareerOverviewJobApplication,
  type CareerOverviewJobPreferences,
  type CareerOverviewNavigate,
  type CareerOverviewPerson
} from "./careerOverviewUtils";

export type CareerOverviewProps = {
  person: CareerOverviewPerson;
  jobPreferences?: CareerOverviewJobPreferences | null;
  applications?: CareerOverviewJobApplication[];
  totalJobApplications?: number | null;
  totalActiveJobApplications?: number | null;
  compensationProfiles?: CareerOverviewCompensationProfile[];
  governanceRoles?: CareerOverviewGovernanceRole[];
  openJobPostCount?: number;
  canViewCareerPreferences: boolean;
  canManageJobPosts: boolean;
  canViewCompensationProfiles: boolean;
  canViewJobPostings: boolean;
  loading?: boolean;
  navigate: CareerOverviewNavigate;
};

export default function CareerOverview({
  person,
  jobPreferences,
  applications = [],
  totalJobApplications,
  totalActiveJobApplications,
  compensationProfiles = [],
  governanceRoles = [],
  openJobPostCount = 0,
  canViewCareerPreferences,
  canManageJobPosts,
  canViewCompensationProfiles,
  canViewJobPostings,
  loading = false,
  navigate
}: CareerOverviewProps) {
  const pgaId = person.pgaId;
  const compensationProfile = getCurrentCompensationProfile(compensationProfiles);
  const activeGovernanceRoleCount = getActiveGovernanceRoles(governanceRoles).length;

  const metrics = buildCareerOverviewMetrics({
    person,
    jobPreferences,
    totalActiveJobApplications,
    canViewCareerPreferences,
    canManageJobPosts,
    canViewCompensationProfiles,
    canViewJobPostings,
    openJobPostCount,
    activeGovernanceRoleCount,
    compensationProfile
  }).map((metric) => ({ ...metric, loading }));

  const hasPreferenceCards = canViewCareerPreferences && !!pgaId;
  const hasApplicationsCard = canManageJobPosts && !!pgaId;
  const hasSummaryCards = canViewCompensationProfiles || hasPreferenceCards || hasApplicationsCard;

  return (
    <div className="flex flex-col min-w-0" data-testid="career-overview">
      {!!metrics.length && <MetricGrid metrics={metrics} minCardWidth={METRIC_CARD_MIN_WIDTH_PX} />}

      {hasSummaryCards && (
        <>
          <SectionHeading
            heading="Additional Career Information"
            body="Roll-up of compensation, job search preferences, and job applications"
          />
          <DetailsCardThreeColumnRow>
            {canViewCompensationProfiles && (
              <DetailsCard
                heading="Compensation"
                options={buildCompensationOverviewOptions(person, compensationProfile, navigate)}
              />
            )}
            {hasPreferenceCards && (
              <DetailsCard
                heading="Job Preferences"
                options={buildPreferencesOverviewOptions(
                  jobPreferences,
                  pgaId,
                  navigate,
                  person.primarySection?.careerConsultants
                )}
              />
            )}
            {hasApplicationsCard && (
              <DetailsCard
                heading="Applications"
                options={buildApplicationsOverviewOptions(
                  applications,
                  totalJobApplications,
                  totalActiveJobApplications,
                  pgaId,
                  navigate
                )}
              />
            )}
          </DetailsCardThreeColumnRow>
        </>
      )}
    </div>
  );
}
